import { getApiClient } from './apiClient';

// Types
export type ManifestStatus = 'DRAFT' | 'LOCKED' | 'IN_TRANSIT' | 'DELIVERED';
export type ManifestType = 'EXPORT' | 'IMPORT';
export type ChargeType = 'FREIGHT' | 'HANDLING' | 'DOCUMENTATION' | 'CUSTOMS' | 'INSURANCE' | 'FUEL_SURCHARGE' | 'OTHER';

export interface ManifestAWB {
    bagNo?: string;
    ediBagNo?: string;
    bagId?: string;
    trackBy: 'AWB_NUMBER' | 'PARCEL_NUMBER';
    awbNo: string;
    actualAwbNo?: string;
    forwardingNo: string;
    bookingDate: string;
    weight: number;
    pcs: number;
    destination?: string;
    service?: string;
    actionDuty?: boolean;
    nsl1?: boolean;
    igstn?: boolean;
    addedAt: string;
    addedBy: string;
}

export interface ManifestTrackingEvent {
    _id: string;
    eventDateTime: string;
    eventDescription: string;
    eventLocation: string;
    createdDate: string;
    createdBy: string;
    appliedToAwbs: boolean;
}

export interface ManifestCharge {
    _id: string;
    chargeType: ChargeType;
    vendor?: string;
    company?: string;
    chargeName: string;
    amount: number;
    remark?: string;
    createdAt: string;
    createdBy: string;
}

export interface ManifestHistoryEntry {
    _id: string;
    action: string;
    description: string;
    oldValue?: any;
    newValue?: any;
    field?: string;
    performedAt: string;
    performedBy: string;
}

export interface Manifest {
    _id: string;
    manifestNo: string;
    date: string;
    time: string;
    forwarder?: {
        _id: string;
        name: string;
    };
    service?: {
        _id: string;
        name: string;
    };
    vendor: {
        _id: string;
        name: string;
    };
    masterNo?: string;
    masterEdiBagNo?: string;
    bagNoRange?: string;
    remark?: string;
    createdByUser: {
        _id: string;
        name: string;
    };
    runNo?: string;
    flightNo?: string;
    vendorCdNo?: string;
    noOfBags: number;
    arrivalDate?: string;
    arrivalTime?: string;
    company?: {
        _id: string;
        name: string;
    };
    license?: {
        _id: string;
        name: string;
    };
    originHub: {
        _id: string;
        name: string;
        code?: string;
    };
    destinationHub?: {
        _id: string;
        name: string;
        code?: string;
    };
    cdImage?: string;
    manifestType: ManifestType;
    totalActualWeight: number;
    totalVolumetricWeight: number;
    totalChargeableWeight: number;
    noOfAwb: number;
    noOfPcs: number;
    awbs: ManifestAWB[];
    trackingEvents: ManifestTrackingEvent[];
    charges: ManifestCharge[];
    totalCharges: number;
    history: ManifestHistoryEntry[];
    status: ManifestStatus;
    createdAt: string;
    updatedAt: string;
}

export interface ManifestStats {
    totalManifests: number;
    draftManifests: number;
    lockedManifests: number;
    inTransitManifests: number;
    deliveredManifests: number;
    totalAWBs: number;
    totalWeight: number;
    totalPieces: number;
    totalCharges: number;
}

export interface CreateManifestRequest {
    date: string;
    time: string;
    forwarder?: string;
    service?: string;
    vendor: string;
    masterNo?: string;
    masterEdiBagNo?: string;
    bagNoRange?: string;
    remark?: string;
    runNo?: string;
    flightNo?: string;
    vendorCdNo?: string;
    noOfBags?: number;
    arrivalDate?: string;
    arrivalTime?: string;
    company?: string;
    license?: string;
    originHub: string;
    destinationHub?: string;
    cdImage?: string;
    manifestType?: ManifestType;
}

export interface UpdateManifestRequest extends Partial<CreateManifestRequest> {
    status?: ManifestStatus;
}

export interface ManifestSearchParams {
    manifestNo?: string;
    vendor?: string;
    flightNo?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    manifestType?: ManifestType;
    search?: string;
    page?: number;
    limit?: number;
}

export interface AWBSearchResult {
    awbNumber: string;
    forwardingNumber: string;
    bookingDate: string;
    weight: number;
    pcs: number;
    destination: string;
    service: string;
    currentStatus: string;
}

export interface AddAWBsRequest {
    awbNumbers: string[];
    bagNo?: string;
    ediBagNo?: string;
    bagId?: string;
    trackBy?: 'AWB_NUMBER' | 'PARCEL_NUMBER';
}

export interface AddTrackingEventRequest {
    eventDateTime: string;
    eventDescription: string;
    eventLocation: string;
    applyToAwbs?: boolean;
}

export interface AddChargeRequest {
    chargeType: ChargeType;
    vendor?: string;
    company?: string;
    chargeName: string;
    amount: number;
    remark?: string;
}

export class ManifestApiError extends Error {
    code?: string;
    details?: Record<string, string>;

    constructor(message: string, code?: string, details?: Record<string, string>) {
        super(message);
        this.name = 'ManifestApiError';
        this.code = code;
        this.details = details;
    }
}

/**
 * Normalize API validation details into a field -> message map.
 * The backend sends either an array of { field, message } entries
 * (controller-level validation) or a record of { field: { message } }
 * (mongoose validation via the error handler).
 */
function normalizeValidationDetails(details: unknown): Record<string, string> | undefined {
    if (!details || typeof details !== 'object') return undefined;

    const normalized: Record<string, string> = {};

    if (Array.isArray(details)) {
        for (const entry of details) {
            if (entry && typeof entry.field === 'string' && typeof entry.message === 'string') {
                normalized[entry.field] = entry.message;
            }
        }
    } else {
        for (const [field, value] of Object.entries(details as Record<string, unknown>)) {
            if (typeof value === 'string') {
                normalized[field] = value;
            } else if (value && typeof value === 'object' && typeof (value as { message?: unknown }).message === 'string') {
                normalized[field] = (value as { message: string }).message;
            }
        }
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
}

class ManifestService {
    private axiosInstance = getApiClient({ useWindowInstance: true });

    async getManifests(params?: ManifestSearchParams): Promise<{ data: Manifest[]; total: number }> {
        try {
            const queryParams = new URLSearchParams();

            if (params) {
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                        queryParams.append(key, value.toString());
                    }
                });
            }

            const response = await this.axiosInstance.get(`/api/manifests?${queryParams}`);

            // Extract data from the nested structure: response.data.data.data
            const manifestData = response.data?.data?.data || [];
            const pagination = response.data?.data?.pagination || {};

            return {
                data: manifestData,
                total: pagination.total || manifestData.length
            };
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Failed to fetch manifests';
            throw new Error(message);
        }
    }

    async getManifest(id: string): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.get(`/api/manifests/${id}`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch manifest');
        }
    }

    async createManifest(data: CreateManifestRequest): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.post('/api/manifests', data);
            return response.data.data;
        } catch (error: any) {
            const apiError = error.response?.data?.error;
            const message = apiError?.message ||
                error.response?.data?.message ||
                'Failed to create manifest';
            throw new ManifestApiError(
                message,
                apiError?.code,
                normalizeValidationDetails(apiError?.details)
            );
        }
    }

    async updateManifest(id: string, data: UpdateManifestRequest): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.put(`/api/manifests/${id}`, data);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to update manifest');
        }
    }

    async deleteManifest(id: string): Promise<void> {
        try {
            await this.axiosInstance.delete(`/api/manifests/${id}`);
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to delete manifest');
        }
    }

    async lockManifest(id: string): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.post(`/api/manifests/${id}/lock`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to lock manifest');
        }
    }

    async cloneManifest(id: string): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.post(`/api/manifests/${id}/clone`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to clone manifest');
        }
    }

    async updateStatus(id: string, status: ManifestStatus): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.put(`/api/manifests/${id}/status`, { status });
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to update status');
        }
    }

    async getStats(): Promise<ManifestStats> {
        try {
            const response = await this.axiosInstance.get('/api/manifests/stats');
            return response.data.data;
        } catch (error: any) {
            console.error('Error fetching stats:', error);
            return {
                totalManifests: 0,
                draftManifests: 0,
                lockedManifests: 0,
                inTransitManifests: 0,
                deliveredManifests: 0,
                totalAWBs: 0,
                totalWeight: 0,
                totalPieces: 0,
                totalCharges: 0
            };
        }
    }

    // AWB Management
    async searchAWBs(searchTerm: string, excludeManifested: boolean = true): Promise<AWBSearchResult[]> {
        try {
            const response = await this.axiosInstance.get('/api/manifests/search-awbs', {
                params: { search: searchTerm, excludeManifested }
            });
            return response.data.data || [];
        } catch (error: any) {
            console.error('Error searching AWBs:', error);
            return [];
        }
    }

    async addAWBs(manifestId: string, data: AddAWBsRequest): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.post(`/api/manifests/${manifestId}/awbs`, data);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.error?.message || error.response?.data?.message || 'Failed to add AWBs');
        }
    }

    async removeAWB(manifestId: string, awbNumber: string): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.delete(`/api/manifests/${manifestId}/awbs/${awbNumber}`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to remove AWB');
        }
    }

    // Tracking Event Management
    async addTrackingEvent(manifestId: string, data: AddTrackingEventRequest): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.post(`/api/manifests/${manifestId}/tracking`, data);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to add tracking event');
        }
    }

    async deleteTrackingEvent(manifestId: string, eventId: string): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.delete(`/api/manifests/${manifestId}/tracking/${eventId}`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to delete tracking event');
        }
    }

    // Charge Management
    async addCharge(manifestId: string, data: AddChargeRequest): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.post(`/api/manifests/${manifestId}/charges`, data);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to add charge');
        }
    }

    async deleteCharge(manifestId: string, chargeId: string): Promise<Manifest> {
        try {
            const response = await this.axiosInstance.delete(`/api/manifests/${manifestId}/charges/${chargeId}`);
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to delete charge');
        }
    }

    // History
    async getHistory(manifestId: string): Promise<ManifestHistoryEntry[]> {
        try {
            const response = await this.axiosInstance.get(`/api/manifests/${manifestId}/history`);
            return response.data.data || [];
        } catch (error: any) {
            console.error('Error fetching history:', error);
            return [];
        }
    }

    // Report
    async downloadReport(manifestId: string, format: 'excel' | 'pdf' = 'excel'): Promise<Blob> {
        try {
            const response = await this.axiosInstance.get(`/api/manifests/${manifestId}/report`, {
                params: { format },
                responseType: 'blob'
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to download report');
        }
    }

    // CD Image upload
    async uploadCdImage(manifestId: string, file: File): Promise<Manifest> {
        try {
            const formData = new FormData();
            formData.append('cdImage', file);
            
            const response = await this.axiosInstance.post(`/api/manifests/${manifestId}/cd-image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to upload CD image');
        }
    }
}

export default new ManifestService();
